import type { ReactNode } from 'react'
import { useSlideout } from '../hooks/useSlideout'
import {
  SlideoutHeaderBar,
  SlideoutVerificationRecord,
  SlideoutSummary,
  SlideoutPayerGrid,
  SlideoutPayerValue,
  type SlideoutPayerData,
} from './SlideoutHeader'

/**
 * Header/identity context shared by the vision slideouts (VSP + EyeMed). Built
 * once in Dashboard (`buildVisionSummary`) from the stored check doc so the
 * slideout renders the same header whether it's opened from the Daily Cockpit
 * or Recent Checks — and the same shared header as the Medical slideout.
 */
export interface VisionCheckSummary {
  patientName: string
  /** ISO 8601 date of service. Reserved for future use (not shown in the
   *  header today, to keep the sub-line identical to the Medical slideout). */
  appointmentDate?: string
  /** ISO 8601. */
  dob?: string
  memberId?: string
  /** Resolved payer directory name. */
  payerName?: string
  /** Raw payer shorthand the check ran against — drives the AI-match badge
   *  when it differs from `payerName`. */
  requestPayerName?: string
  providerName?: string
  providerNpi?: string
  /** ISO 8601 — verification timestamp for the "Payer verified" stamp. */
  checkedAt?: string
  /** Clinic IANA timezone for the stamp. */
  timezone?: string
  /** Stored check id — enables the chart permalink. */
  checkId?: string
}

const EyeIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

/**
 * Shared frame for the vision slideouts — overlay + panel + the shared header
 * (bar, verification record, patient/provider/payer summary). Renders the exact
 * same header components as the Medical slideout, so all three look identical.
 * The vendor benefit body is passed as `children`; VSP and EyeMed differ only
 * by `subtitle`, `ariaLabel`, and that body.
 */
export default function VisionSlideoutFrame({ subtitle, ariaLabel, summary, payerDetails, onClose, children }: {
  subtitle: string
  ariaLabel: string
  summary: VisionCheckSummary
  /** Header slots a vendor panel composes itself — EyeMed: the member ID
   *  EyeMed returned (with its note), the group and the plan as the portal
   *  printed them (`utils/eyemedHeader.ts`). Each node given replaces the
   *  grid's default for that slot; a slot left out keeps it (Member ID the
   *  record's id, Plan and Group the dash). VSP passes nothing. */
  payerDetails?: Pick<SlideoutPayerData, 'plan' | 'memberId' | 'group'>
  onClose: () => void
  children: ReactNode
}) {
  const panelRef = useSlideout(onClose)

  return (
    <>
      <div className="slideout-overlay" onClick={onClose} />

      <div
        className="slideout-panel"
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        ref={panelRef}
        tabIndex={-1}
      >
        <SlideoutHeaderBar icon={EyeIcon} title="Vision Benefits" subtitle={subtitle} onClose={onClose} />
        <SlideoutVerificationRecord checkedAt={summary.checkedAt} timezone={summary.timezone} checkId={summary.checkId} />

        <div className="slideout-panel__content">
          <SlideoutSummary
            patientName={summary.patientName}
            dob={summary.dob}
            providerName={summary.providerName}
            providerNpi={summary.providerNpi}
          />

          <div className="med-divider" />

          {/* Plan + Group render an em dash unless the vendor panel supplies
              them (`payerDetails`): VSP checks don't carry them, EyeMed's
              panel composes them from what the portal printed. Accepted, see
              SlideoutPayerGrid. */}
          <SlideoutPayerGrid
            payer={summary.payerName
              ? <SlideoutPayerValue name={summary.payerName} resolvedFrom={summary.requestPayerName} />
              : undefined}
            plan={payerDetails?.plan}
            memberId={payerDetails?.memberId ?? summary.memberId}
            group={payerDetails?.group}
          />

          {children}
        </div>
      </div>
    </>
  )
}
